import { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Command } from 'cmdk';
import {
  Modal,
  ModalBackdrop,
  ModalContainer,
  ModalDialog,
  Badge,
} from '@xstore/shared/ui';
import {
  Search,
  LayoutDashboard,
  Package,
  ShoppingCart,
  CreditCard,
  KeyRound,
  Wallet,
  Workflow,
  Bot,
  Radio,
  Users,
  MessageSquare,
  Brain,
  BarChart3,
  Activity,
  HardDrive,
  Archive,
  History,
  Bell,
  Shield,
  Settings,
  Moon,
  Sun,
  Plus,
} from 'lucide-react';

import { useTheme } from '../../providers/theme-provider';
import { useBots } from '../../providers/bot-provider';
import { NAV_MODULES } from '../../config/navigation';


interface CommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

const ICON_MAP: Record<string, React.ComponentType<{ size?: number }>> = {
  dashboard: LayoutDashboard,
  products: Package,
  orders: ShoppingCart,
  payments: CreditCard,
  vault: KeyRound,
  wallet: Wallet,
  workflows: Workflow,
  bot: Bot,
  broadcasts: Radio,
  customers: Users,
  tickets: MessageSquare,
  ai: Brain,
  analytics: BarChart3,
  reports: Activity,
  storage: HardDrive,
  backup: Archive,
  'access-history': History,
  notifications: Bell,
  monitoring: Activity,
  rbac: Shield,
  settings: Settings,
};

export function CommandPalette({ isOpen, onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const { resolvedTheme, toggleTheme } = useTheme();
  const { selectedBot } = useBots();
  void selectedBot;
  const [query, setQuery] = useState('');

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onClose();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  useEffect(() => {
    if (isOpen) setQuery('');
  }, [isOpen]);

  const moduleItems = useMemo(
    () =>
      NAV_MODULES.map((mod) => ({
        id: `mod-${mod.id}`,
        label: mod.label,
        path: mod.path,
        icon: ICON_MAP[mod.id] ?? LayoutDashboard,
        group: 'Modules',
      })),
    [],
  );

  const recentItems = useMemo(
    () => [
      { id: 'recent-orders', label: 'Recent Orders', path: '/orders', icon: ShoppingCart, group: 'Recent' },
      { id: 'recent-customers', label: 'Recent Customers', path: '/customers', icon: Users, group: 'Recent' },
      { id: 'recent-settings', label: 'Settings', path: '/settings', icon: Settings, group: 'Recent' },
    ],
    [],
  );

  const actionItems = useMemo(
    () => [
      {
        id: 'create-product',
        label: 'Create Product',
        action: () => {
          navigate('/products');
          onClose();
        },
        icon: Plus,
        group: 'Quick Actions',
      },
      {
        id: 'create-broadcast',
        label: 'Create Broadcast',
        action: () => {
          navigate('/broadcasts');
          onClose();
        },
        icon: Radio,
        group: 'Quick Actions',
      },
      {
        id: 'create-workflow',
        label: 'Create Workflow',
        action: () => {
          navigate('/workflows');
          onClose();
        },
        icon: Workflow,
        group: 'Quick Actions',
      },
      {
        id: 'open-orders',
        label: 'Open Orders',
        action: () => {
          navigate('/orders');
          onClose();
        },
        icon: ShoppingCart,
        group: 'Quick Actions',
      },
      {
        id: 'open-customers',
        label: 'Open Customers',
        action: () => {
          navigate('/customers');
          onClose();
        },
        icon: Users,
        group: 'Quick Actions',
      },
      {
        id: 'open-settings',
        label: 'Open Settings',
        action: () => {
          navigate('/settings');
          onClose();
        },
        icon: Settings,
        group: 'Quick Actions',
      },
      {
        id: 'toggle-theme',
        label: 'Toggle Theme',
        action: () => {
          void toggleTheme();
          onClose();
        },
        icon: resolvedTheme === 'dark' ? Sun : Moon,
        group: 'Quick Actions',
      },
    ],
    [navigate, onClose, toggleTheme, resolvedTheme],
  );

  const allItems = useMemo(
    () => [...recentItems, ...moduleItems, ...actionItems],
    [recentItems, moduleItems, actionItems],
  );

  const filtered = useMemo(() => {
    if (!query) return allItems;
    const q = query.toLowerCase();
    return allItems.filter((i) => i.label.toLowerCase().includes(q));
  }, [query, allItems]);

  const groups = ['Recent', 'Modules', 'Quick Actions'];

  return (
    <Modal isOpen={isOpen} onOpenChange={onClose}>
      <ModalBackdrop />
      <ModalContainer>
        <ModalDialog className="overflow-hidden p-0">
          <Command label="Command palette" className="w-full max-w-2xl" loop>
            <div className="flex items-center gap-3 border-b border-white/[0.06] px-4 py-4">
              <Search size={18} className="text-muted" />
              <Command.Input
                value={query}
                onValueChange={setQuery}
                placeholder="Type a command or search modules..."
                className="flex-1 bg-transparent text-sm outline-none text-foreground placeholder:text-muted"
              />
              <kbd className="rounded-md bg-white/[0.08] px-1.5 py-0.5 text-[11px] text-muted">
                ESC
              </kbd>
            </div>

            <Command.List className="max-h-[420px] overflow-auto p-2">
              <Command.Empty className="px-4 py-10 text-center text-sm text-muted">
                No results found.
              </Command.Empty>

              {groups.map((group) => {
                const items = filtered.filter((i) => i.group === group);
                if (items.length === 0) return null;
                return (
                  <Command.Group
                    key={group}
                    heading={
                      <span className="px-2 text-[11px] font-semibold uppercase tracking-wider text-muted">
                        {group}
                      </span>
                    }
                    className="py-2"
                  >
                    {items.map((item) => {
                      const Icon = item.icon;
                      return (
                        <Command.Item
                          key={item.id}
                          onSelect={() => {
                            if ('path' in item && item.path) {
                              void navigate(item.path);
                              onClose();
                            } else if ('action' in item && item.action) {
                              void item.action();
                            }
                          }}
                          className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-foreground transition-colors hover:bg-white/[0.05] data-[selected=true]:bg-primary/10 data-[selected=true]:text-primary cursor-pointer"
                        >
                          <Icon size={18} />
                          <span className="flex-1">{item.label}</span>
                          {group === 'Modules' && (
                            <Badge variant="secondary" className="text-[10px]">
                              Open
                            </Badge>
                          )}
                        </Command.Item>
                      );
                    })}
                  </Command.Group>
                );
              })}
            </Command.List>

            <div className="flex items-center justify-between border-t border-white/[0.06] px-4 py-2.5 text-[11px] text-muted">
              <div className="flex gap-3">
                <span className="flex items-center gap-1">
                  <kbd className="rounded bg-white/[0.06] px-1.5 py-0.5">⌘K</kbd> Open
                </span>
                <span className="flex items-center gap-1">
                    <kbd className="rounded bg-white/[0.06] px-1.5 py-0.5">↵</kbd> Select
                </span>
              </div>
              <span>{filtered.length} results</span>
            </div>
          </Command>
        </ModalDialog>
      </ModalContainer>
    </Modal>
  );
}
